import { useState } from "react"
import { Eye, EyeOff, LockKeyhole, UserPlus, X } from "lucide-react"
import toast from "react-hot-toast"
import API_URL from "../../config"
import { useAuth } from "../../contexts/auth-context"

const initialRegister = {
  nombres: "",
  apellidos: "",
  correo: "",
  password: "",
  confirmarPassword: "",
}

export default function AuthModal({ open, onClose, onSuccess, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode)
  const [correo, setCorreo] = useState("")
  const [password, setPassword] = useState("")
  const [register, setRegister] = useState(initialRegister)
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()

  if (!open) return null

  const submit = async (event) => {
    event.preventDefault()
    if (mode === "register" && register.password !== register.confirmarPassword) {
      toast.error("Las contraseñas no coinciden")
      return
    }

    setLoading(true)
    try {
      const payload = mode === "login" ? { correo, password } : register
      const response = await fetch(`${API_URL}/${mode === "login" ? "login" : "registro"}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.message || "No se pudo completar la solicitud")

       if (mode === "login") login(data)
      toast.success(mode === "login" ? "Sesión iniciada" : "Cuenta creada correctamente")
      onClose()
      onSuccess?.(data)
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/65 p-4" onMouseDown={onClose}>
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
        <div className="bg-gradient-to-r from-gray-950 to-gray-800 px-6 py-5 text-white">
          <button onClick={onClose} className="absolute right-4 top-4 rounded-full p-2 hover:bg-white/10" aria-label="Cerrar">
            <X size={20} />
          </button>
          <img
            src="/LOGO_imporisel.webp"
            alt="IMPORISEL"
            className="h-10 w-auto max-w-[190px] object-contain object-left"
          />
          <h2 className="mt-1 text-2xl font-black">{mode === "login" ? "Bienvenido de nuevo" : "Crea tu cuenta"}</h2>
          <p className="mt-1 text-sm text-gray-300">Debes ingresar para finalizar una compra y consultar tus órdenes.</p>
        </div>

        <div className="grid grid-cols-2 border-b bg-gray-50 p-1">
          <button onClick={() => setMode("login")} className={`rounded-lg px-3 py-2.5 text-sm font-bold ${mode === "login" ? "bg-white text-gray-950 shadow" : "text-gray-500"}`}>
            Iniciar sesión
          </button>
          <button onClick={() => setMode("register")} className={`rounded-lg px-3 py-2.5 text-sm font-bold ${mode === "register" ? "bg-white text-gray-950 shadow" : "text-gray-500"}`}>
            Registrarme
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3 p-5 sm:p-6">
          {mode === "register" && (
            <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Nombres</label>
                  <input required value={register.nombres} onChange={(e) => setRegister({ ...register, nombres: e.target.value })} placeholder="Ej. Juan Carlos" className="w-full rounded-lg border px-4 py-3 outline-none focus:border-yellow-500" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Apellidos</label>
                  <input required value={register.apellidos} onChange={(e) => setRegister({ ...register, apellidos: e.target.value })} placeholder="Ej. Pérez" className="w-full rounded-lg border px-4 py-3 outline-none focus:border-yellow-500" />
                </div>
            </div>
          )}

          <div>
            {mode === "register" && <label className="mb-1 block text-xs font-semibold text-gray-600">Correo electrónico</label>}
            <input
              required
              type="email"
              value={mode === "login" ? correo : register.correo}
              onChange={(e) => mode === "login" ? setCorreo(e.target.value) : setRegister({ ...register, correo: e.target.value })}
              placeholder="Correo electrónico"
              className="w-full rounded-lg border px-4 py-3 outline-none focus:border-yellow-500"
            />
          </div>

           <div className="relative">
            {mode === "register" && <label className="mb-1 block text-xs font-semibold text-gray-600">Contraseña</label>}
            <input required type={showPassword ? "text" : "password"} value={mode === "login" ? password : register.password} onChange={(e) => mode === "login" ? setPassword(e.target.value) : setRegister({ ...register, password: e.target.value })} placeholder="Contraseña" className="w-full rounded-lg border px-4 py-3 pr-12 outline-none focus:border-yellow-500" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className={`absolute right-3 ${mode === "register" ? "top-8" : "top-3.5"} text-gray-500`} aria-label="Mostrar contraseña">
              {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
            </button>
          </div>

          {mode === "register" && (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600">Confirmar contraseña</label>
                <input required type={showPassword ? "text" : "password"} value={register.confirmarPassword} onChange={(e) => setRegister({ ...register, confirmarPassword: e.target.value })} placeholder="Repite tu contraseña" className="w-full rounded-lg border px-4 py-3 outline-none focus:border-yellow-500" />
              </div>
              <p className="text-xs text-gray-500">Mínimo 8 caracteres, con mayúscula, minúscula y número.</p>
            </>
          )}

          <button disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-lg bg-yellow-400 px-4 py-3 font-black text-gray-950 hover:bg-yellow-500 disabled:opacity-60">
            {mode === "login" ? <LockKeyhole size={18} /> : <UserPlus size={18} />}
            {loading ? "Procesando..." : mode === "login" ? "Ingresar" : "Crear cuenta"}
          </button>
        </form>
      </div>
    </div>
  )
}
